import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Lock,
  Unlock,
  ShieldAlert,
  BookOpen,
  Crown,
  Ticket,
  Loader2,
  Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ComicBookletProps {
  title: string;
  pages: Array<{ src: string; alt: string }>;
  fullIssueUnlocked: boolean;
  accessLoading: boolean;
  isAdmin: boolean;
  isAuthenticated: boolean;
  onUnlockPackage: (productId: 43 | 44) => void;
  onOpenFullIssue: () => void;
  fullIssueLoading: boolean;
}

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

const pageVariants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? "10%" : "-10%",
      opacity: 0,
      rotateY: direction > 0 ? 10 : -10,
      scale: 0.96,
      filter: "blur(4px)",
    };
  },
  center: {
    x: 0,
    opacity: 1,
    rotateY: 0,
    scale: 1,
    filter: "blur(0px)",
  },
  exit: (direction: number) => {
    return {
      x: direction < 0 ? "10%" : "-10%",
      opacity: 0,
      rotateY: direction < 0 ? 10 : -10,
      scale: 0.96,
      filter: "blur(4px)",
    };
  },
};

export function ComicBooklet({
  title,
  pages,
  fullIssueUnlocked,
  accessLoading,
  isAdmin,
  isAuthenticated,
  onUnlockPackage,
  onOpenFullIssue,
  fullIssueLoading
}: ComicBookletProps) {
  const [[page, direction], setPage] = useState([0, 0]);
  const isPaywall = page === pages.length;

  const paginate = useCallback((newDirection: number) => {
    setPage((prev) => {
      const currentPage = prev[0];
      const newPage = currentPage + newDirection;
      if (newPage < 0 || newPage > pages.length) return prev;
      return [newPage, newDirection];
    });
  }, [pages.length]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        paginate(1);
      } else if (e.key === "ArrowLeft") {
        paginate(-1);
      }
    },
    [paginate]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const goToPage = (index: number) => {
    if (index === page) return;
    setPage([index, index > page ? 1 : -1]);
  };

  const renderPaywallContent = () => {
    if (accessLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-20 space-y-6">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
          <p className="text-muted-foreground text-lg tracking-wide uppercase font-medium">Verifying Access...</p>
        </div>
      );
    }

    if (fullIssueUnlocked || isAdmin) {
      return (
        <div className="flex flex-col items-center justify-center space-y-8 py-10 w-full max-w-md mx-auto">
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/30">
            <Unlock className="w-10 h-10 text-emerald-400" />
          </div>
          
          <div className="text-center space-y-3">
            <h3 className="text-3xl font-bold tracking-tight text-white">Full Issue Unlocked</h3>
            {isAdmin && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-widest border border-blue-500/30">
                <ShieldAlert className="w-3.5 h-3.5" />
                Admin Access
              </span>
            )}
            <p className="text-muted-foreground">
              You have full access to the 20-page issue of Balcony Between Worlds.
            </p>
          </div>

          <Button 
            size="lg" 
            className="w-full text-base h-14 uppercase tracking-wider font-bold group"
            onClick={onOpenFullIssue}
            disabled={fullIssueLoading}
          >
            {fullIssueLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Open Full 20-Page Issue
                <BookOpen className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" />
              </>
            )}
          </Button>
        </div>
      );
    }

    // Locked State
    return (
      <div className="flex flex-col items-center w-full max-w-4xl mx-auto space-y-8 md:space-y-10">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center border border-primary/30 mx-auto mb-6">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-white">
            End of Preview
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            The story continues. Unlock the full issue to reveal what happens next in Balcony Between Worlds.
          </p>
          {!isAuthenticated && (
            <div className="inline-flex items-center gap-2 mt-2 px-4 py-2 rounded-lg bg-secondary/50 border border-secondary text-sm text-secondary-foreground">
              <Info className="w-4 h-4" />
              Sign-in is required to unlock full issues.
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6 w-full">
          {/* Offer 1 */}
          <div className="relative flex flex-col p-6 md:p-8 rounded-2xl border border-white/10 bg-black/40 hover:bg-black/60 transition-colors group">
            <div className="flex justify-between items-start mb-4">
              <div className="space-y-1">
                <h4 className="text-xl font-bold text-white group-hover:text-primary transition-colors">3-Issue Package</h4>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Standard Access</p>
              </div>
              <span className="text-2xl font-black text-white">$30</span>
            </div>
            
            <p className="text-muted-foreground text-sm leading-relaxed mb-8 flex-1">
              Includes Issues #1-3 of Balcony Between Worlds. Follow the complete first story arc.
            </p>

            <Button 
              variant="outline"
              size="lg"
              className="w-full border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
              onClick={() => onUnlockPackage(43)}
            >
              Unlock Package
            </Button>
          </div>

          {/* Offer 2 */}
          <div className="relative flex flex-col p-6 md:p-8 rounded-2xl border-2 border-primary bg-primary/5 shadow-lg shadow-primary/20 overflow-hidden">
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 text-xs font-bold uppercase tracking-wider rounded-bl-lg">
              Best Value
            </div>
            
            <div className="flex justify-between items-start mb-4">
              <div className="space-y-1 pr-16">
                <h4 className="text-xl font-bold text-white flex items-center gap-2">
                  5-Issue Set
                  <Crown className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                </h4>
                <p className="text-sm font-medium text-primary uppercase tracking-wider">+ VIP Event Ticket</p>
              </div>
              <span className="text-2xl font-black text-white">$75</span>
            </div>
            
            <p className="text-muted-foreground text-sm leading-relaxed mb-8 flex-1">
              The complete collection plus an exclusive Jack of Clubz VIP Event Ticket. Experience the world beyond the pages.
            </p>

            <Button 
              variant="default"
              size="lg"
              className="w-full shadow-[0_0_15px_hsl(var(--primary)/0.4)]"
              onClick={() => onUnlockPackage(44)}
            >
              <Ticket className="w-4 h-4 mr-2" />
              Unlock Premium Set
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="flex flex-col w-full max-w-6xl mx-auto bg-[#050505] rounded-3xl border border-white/10 overflow-hidden shadow-2xl relative select-none"
      style={{ height: "min(85vh, 920px)", minHeight: 600 }}
    >
      {/* Top Header */}
      <header className="flex-none flex justify-between items-center px-6 h-16 border-b border-white/5 bg-black/40 backdrop-blur-md z-20 relative">
        <h2 className="font-bold tracking-widest uppercase text-sm text-white/80 truncate pr-4">{title}</h2>
        <div className="flex items-center gap-2 text-xs font-mono text-white/50 bg-white/5 px-3 py-1 rounded-full flex-shrink-0 border border-white/10">
          <span className={page === pages.length ? "text-primary font-bold" : "text-white"}>
            {page === pages.length ? "END" : page + 1}
          </span>
          <span>/</span>
          <span>{pages.length}</span>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 relative overflow-hidden" style={{ perspective: "1200px" }}>
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={page}
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.25 },
              rotateY: { type: "spring", stiffness: 200, damping: 40 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.7}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
            className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing"
          >
            {isPaywall ? (
              <div className="w-full h-full overflow-y-auto scrollbar-none cursor-auto">
                <div className="min-h-full flex items-center justify-center py-8 px-4">
                  {renderPaywallContent()}
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center p-4 md:p-8">
                <img
                  src={pages[page].src}
                  alt={pages[page].alt}
                  className="max-w-full max-h-full object-contain shadow-2xl rounded-sm pointer-events-none"
                  draggable={false}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation Overlays */}
        <div className="absolute inset-y-0 left-0 w-16 md:w-32 flex items-center justify-start px-2 md:px-4 z-10 pointer-events-none">
          <button
            onClick={() => paginate(-1)}
            disabled={page === 0}
            className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/50 border border-white/10 text-white flex items-center justify-center backdrop-blur-md pointer-events-auto hover:bg-white/10 hover:scale-110 disabled:opacity-0 transition-all disabled:pointer-events-none"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>
        
        <div className="absolute inset-y-0 right-0 w-16 md:w-32 flex items-center justify-end px-2 md:px-4 z-10 pointer-events-none">
          <button
            onClick={() => paginate(1)}
            disabled={page === pages.length}
            className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/50 border border-white/10 text-white flex items-center justify-center backdrop-blur-md pointer-events-auto hover:bg-primary/20 hover:border-primary/50 hover:text-primary hover:scale-110 disabled:opacity-0 transition-all disabled:pointer-events-none"
            aria-label="Next page"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>
      </main>

      {/* Bottom Thumbnails */}
      <footer className="flex-none bg-black/80 backdrop-blur-xl border-t border-white/5 p-3 md:p-4 z-20 relative">
        <div className="flex justify-center gap-2 md:gap-3 overflow-x-auto scrollbar-none pb-1 px-2 max-w-full">
          {pages.map((p, i) => (
            <button
              key={i}
              onClick={() => goToPage(i)}
              className={cn(
                "relative w-10 h-14 md:w-16 md:h-24 flex-shrink-0 rounded overflow-hidden border-2 transition-all duration-300",
                page === i 
                  ? "border-primary scale-110 shadow-[0_0_15px_hsl(var(--primary)/0.5)] z-10" 
                  : "border-transparent opacity-40 hover:opacity-100 hover:border-white/30 hover:-translate-y-1"
              )}
              aria-label={`Go to page ${i + 1}`}
              aria-current={page === i ? "page" : undefined}
            >
              <img src={p.src} alt={`Thumbnail ${i + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
          <button
            onClick={() => goToPage(pages.length)}
            className={cn(
              "relative w-10 h-14 md:w-16 md:h-24 flex-shrink-0 rounded border-2 transition-all duration-300 flex items-center justify-center bg-white/5",
              page === pages.length 
                ? "border-primary scale-110 shadow-[0_0_15px_hsl(var(--primary)/0.5)] z-10 text-primary bg-primary/10" 
                : "border-transparent opacity-40 hover:opacity-100 hover:border-white/30 hover:-translate-y-1 text-white"
            )}
            aria-label="View unlocking options"
            aria-current={page === pages.length ? "page" : undefined}
          >
            {fullIssueUnlocked || isAdmin ? (
              <Unlock className="w-5 h-5 md:w-6 md:h-6" />
            ) : (
              <Lock className="w-5 h-5 md:w-6 md:h-6" />
            )}
          </button>
        </div>
      </footer>
    </div>
  );
}
